import React, { useCallback, useMemo, useRef } from 'react';
import type { User, UserInput } from '#/graphql/types';
import Form, { FormAction, FormRefType } from '#/common/components/Form';
import fields from './fields';
import { useNavigate } from 'react-router-dom';
import useToast from '#/common/hooks/useToast';
import useUserUpdate from '../../hooks/useUpdateUserMutation';
import { get } from 'lodash';
import useUserCreate from '../../hooks/useCreateUserMutation';
import useUserDelete from '../../hooks/useDeleteUserMutation';
import allFields from '../../utils/fields';
import { getCleanFormErrors } from '#/common/utils/customRelatedItemsValidation';
import { UserRole } from '#/common/types/userTypes';
import useSession from '#/common/hooks/useSession';

export interface UserFormProps {
  user: User | {};
  title: string;
}

const formReadonlyRole: Record<UserRole, boolean> = {
  [UserRole.asset_manager]: true,
  [UserRole.trane_admin]: false,
  [UserRole.technician]: true,
};

const UserForm: React.FC<UserFormProps> = ({ user, title }) => {
  const { alert, confirm } = useToast();
  const navigate = useNavigate();
  const formRef = useRef<FormRefType | null>(null);
  const id = get(user, 'id', undefined);

  // Defined in the modules hooks dir
  const { updateUser } = useUserUpdate();
  const { createUser } = useUserCreate();
  const { deleteUser } = useUserDelete();

  const deleteItem = useCallback(
    (id: string) => {
      if (!id) {
        return undefined;
      }
      return deleteUser(id);
    },
    [id, deleteUser],
  );

  // Submit that updates or creates a new record based on if there is ID present
  const submit = useCallback(
    (data: UserInput) => {
      if (!id) {
        return createUser(data);
      }
      return updateUser(id, data);
    },
    [id, updateUser, createUser],
  );

  const { user: sessionUser } = useSession();
  const role = sessionUser?.role as UserRole | undefined;

  const isFormReadonly = !role || formReadonlyRole[role];

  const actions = useMemo(() => {
    const result: FormAction<User>[] = [];

    if (!isFormReadonly && deleteItem && id) {
      result.push({
        label: 'Delete',
        icon: 'trash',
        type: 'danger',
        onClick: async (e) => {
          const sure = await confirm(
            'error',
            `Are you sure you want to delete user: ${e.record?.name}?`,
          );

          if (!sure) {
            return;
          }

          try {
            if (!e.record?.id) {
              return;
            }

            e.setIsLoading(true);
            const result = await deleteItem(e.record.id);
            e.setIsLoading(false);
            ``;
            if (result?.errors) {
              void alert('error', result.errors.join(`;\n`));
              return;
            }

            navigate('/admin/users');
          } catch (e: any) {
            console.log('Error', e);
            void alert('error', 'Missing or invalid data!');
          }
        },
      });
    }
    if (!isFormReadonly && submit) {
      result.push({
        label: 'Submit',
        type: 'submit',
        onClick: async (e) => {
          if (!e.record) {
            return;
          }
          const validation = e.form?.validate();
          if (!validation?.isValid && getCleanFormErrors(validation, allFields, e.record).length) {
            void alert('error', `${title} is not valid, check field entries.`);
            return;
          }

          try {
            e.setIsLoading(true);
            const result = await submit(e.record);
            e.setIsLoading(false);

            if (result?.errors) {
              void alert('error', result.errors.join(`;\n`));
              return;
            }
            if (!id && result?.data && result?.data.id) {
              navigate(`/admin/users/${result.data?.id}`);
            }

            void alert('success', 'Changes saved!');
            formRef.current?.repaint();
          } catch (err: any) {
            e.setIsLoading(false);
            console.log('Error', err);
            void alert('error', 'Missing or invalid data!');
          }
        },
      });
    }

    return result;
  }, [alert, confirm, navigate, submit, user, deleteItem]);
  return (
    <Form
      ref={formRef}
      record={user as User}
      fields={fields}
      actions={actions}
      readonly={isFormReadonly}
    />
  );
};

export default React.memo(UserForm);
